import React, { useEffect, useState, useMemo, useRef } from 'react';
import { plusIcon } from '../helpers/icons';
import {
  Alert,
  Form,
  Button,
  ButtonGroup,
  Container,
  Modal,
} from 'react-bootstrap';
import { useApolloClient } from '@apollo/client';
import { createOverlay, useErrorHandler } from '../../apollo/helpers';
import SessionDetail from './sessiondetail';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './dashboard.css';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions, adminActions } from '../../redux/_actions';
import TableComponent from '../helpers/tablecomponent';
import { dateTime } from '../../utilities/conversions';
import { sessionStatus } from '../../redux/_constants';
import { Loading, ScrollLoading } from '../helpers/loading';
import { SearchInput, addBold, noResults } from '../helpers/search';
import StreamCreate from './streamcreate';

function LiveDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const alert = useSelector((state) => state.alert);
  const sessions = useSelector((state) => state.admin.sessions);
  const sessionStreams = useSelector((state) => state.admin.streams);
  const searchQuery = useSelector(
    (state) => state.admin.sessions.tokens.searchQuery
  );

  const [searchTerm, setSearchTerm] = useState(searchQuery || '');
  const [tempSearchTerm, setTempSearchTerm] = useState(searchQuery || '');
  const [filters, setFilters] = useState({
    gender: null,
    status: 'LIVE', // Default to showing LIVE sessions
    start: null,
    demo: 'hide', // Default to hiding demos
    startDate: null,
    endDate: null,
  });
  // Server-side date range filter: 'near' (±7 days), '+30', '+90', 'all'
  const [dateRangeFilter, setDateRangeFilter] = useState('near');

  // Helper to compute date range for server query
  const getDateRangeForFilter = (filterValue) => {
    const now = new Date();
    switch (filterValue) {
      case 'near': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 7);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+30': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 30);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case '+90': {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        const end = new Date(now);
        end.setDate(end.getDate() + 90);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
      }
      case 'all':
      default:
        return null; // No date filter
    }
  };
  const [selectedSession, setSelectedSession] = useState(null);
  const [showStreamCreate, setShowStreamCreate] = useState(null);
  const apolloClient = useApolloClient();
  const userId = useSelector((state) => state.user.profile?.id);
  const handleCreateOverlayErrors = useErrorHandler(
    'There was a problem sending the overlay'
  );

  const dispatch = useDispatch();
  const numSessions = sessions?.allIds?.length || 0;
  const isSearch = searchTerm !== '';
  const [tableData, setData] = useState([]);

  const isFirstRender = useRef(true);

  // Single effect handles both initial load and date range changes
  useEffect(() => {
    if (isFirstRender.current) {
      dispatch(alertActions.clear());
      isFirstRender.current = false;
    }
    setIsLoading(true);
    const dateRange = getDateRangeForFilter(dateRangeFilter);
    dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
  }, [dateRangeFilter, dispatch]);

  // Only stop loading when we actually have sessions data
  useEffect(() => {
    if (sessions?.allIds?.length > 0) {
      setIsLoading(false);
      setIsScrollLoading(false);
    }
    setSearchTerm(tempSearchTerm);
  }, [sessions, tempSearchTerm]);

  const headers = useMemo(
    () => [
      { Header: '#', accessor: 'order' },
      {
        Header: 'Name',
        accessor: 'name',
        maxWidth: 50,
        Cell: (r) => addBold(r.cell.value, searchTerm),
      },
      { Header: 'Type', accessor: 'type' },
      { Header: 'Gender', accessor: 'gender' },
      {
        Header: 'Key',
        accessor: 'key',
        Cell: ({ row }) => (
          <Link
            to={{
              pathname: '/session',
              search: `?s=${row.values.key}`,
              state: { fromAdmin: true },
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.values.key}
          </Link>
        ),
      },
      {
        Header: 'Start',
        accessor: 'start',
        Cell: ({ row }) => dateTime(row.values.start),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: (r) => {
          const { cell } = r;
          return (
            <Form.Control
              as="select"
              label="Status"
              value={cell.value}
              disabled
            >
              {Object.keys(sessionStatus).map((statusType) => (
                <option key={statusType} value={statusType}>
                  {statusType}
                </option>
              ))}
            </Form.Control>
          );
        },
      },
      {
        Header: 'Rotations',
        accessor: 'rotations',
        Cell: ({ row }) => {
          const numRotations = row.values.rotations;
          return (
            <div className="d-flex align-items-center gap-2">
              <ButtonGroup size="sm">
                {[...Array(numRotations)].map((_, index) => (
                  <Button
                    key={index}
                    variant={
                      tableData[row.index]?.selectedRotation === index + 1
                        ? 'secondary'
                        : 'outline-secondary'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const newData = [...tableData];
                      if (!newData[row.index]) {
                        newData[row.index] = {};
                      }
                      newData[row.index] = {
                        ...newData[row.index],
                        ...row.original,
                        selectedRotation: index + 1,
                      };
                      setData(newData);
                    }}
                  >
                    {index + 1}
                  </Button>
                ))}
              </ButtonGroup>
              <Button
                variant="light"
                size="sm"
                className="mx-1"
                onClick={(e) => {
                  e.stopPropagation();
                  const input = {
                    command: JSON.stringify({
                      msg: "Let's go",
                      userId,
                      round: tableData[row.index]?.selectedRotation || 1,
                      id: Date.now(),
                    }),
                    sessionId: row.original.id,
                    displayAt: new Date(),
                  };

                  createOverlay(apolloClient, input)
                    .then(() => {
                      dispatch(
                        alertActions.success(
                          `Synced session ${row.original.name} to rotation ${
                            tableData[row.index]?.selectedRotation || 1
                          }`
                        )
                      );
                    })
                    .catch((e) => {
                      console.log(e);
                      handleCreateOverlayErrors();
                    });
                }}
              >
                Sync
              </Button>
            </div>
          );
        },
      },
      {
        Header: 'Streams',
        accessor: 'streams',
        Cell: ({ row }) => {
          const streams = Array.isArray(row.original.streams)
            ? row.original.streams
            : [];
          return (
            <div className="streams-cell d-flex flex-column">
              <div className="d-flex flex-wrap align-items-center gap-1">
                {streams.map((stream, index) => (
                  <div key={index} style={{ marginRight: '0.4rem' }}>
                    {stream || 'N/A'}
                  </div>
                ))}
                <Button
                  variant="light"
                  className="stream-add-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStreamCreate(row.original.id);
                  }}
                >
                  {plusIcon}
                </Button>
              </div>
            </div>
          );
        },
      },
    ],
    [searchTerm, sessions, dispatch, tableData]
  );

  useEffect(() => {
    setData(data);
  }, [sessions, sessionStreams, filters]);

  const data = useMemo(() => {
    let filteredData = sessions.allIds.map((id, i) => {
      // Initialize selectedRotation in the data
      const {
        name,
        type,
        gender,
        sessionKey,
        startAt,
        status,
        streams,
        rotations,
      } = sessions.byId[id];

      return {
        order: i + 1,
        name: name,
        type: type,
        gender: gender,
        key: sessionKey,
        start: startAt,
        status: status,
        id: id,
        streams:
          streams?.items?.map((stream) => {
            const streamId = typeof stream === 'string' ? stream : stream.id;
            return sessionStreams.byId[streamId]?.title;
          }) || [],
        rotations: rotations?.items?.filter((r) => !r._deleted)?.length || 0,
        selectedRotation: null,
      };
    });

    if (filters.gender) {
      filteredData = filteredData.filter(
        (row) => row.gender === filters.gender
      );
    }
    if (filters.status) {
      filteredData = filteredData.filter(
        (row) => row.status === filters.status
      );
    }
    if (filters.start === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      filteredData = filteredData.filter((row) => {
        const startDate = new Date(row.start);
        return startDate >= today && startDate < tomorrow;
      });
    }
    if (filters.demo === 'hide') {
      filteredData = filteredData.filter((row) => {
        return !row.name.toLowerCase().includes('demo');
      });
    }
    if (filters.startDate) {
      filteredData = filteredData.filter((row) => {
        const sessionDate = new Date(row.start);
        const startDate = new Date(filters.startDate);
        startDate.setHours(0, 0, 0, 0);

        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          return sessionDate >= startDate && sessionDate <= endDate;
        }

        return sessionDate.toDateString() === startDate.toDateString();
      });
    }

    return filteredData;
  }, [sessions, sessionStreams, filters]);

  const FilterPill = ({ label, value, filterKey, className = '' }) => (
    <Button
      variant={filters[filterKey] === value ? 'secondary' : 'outline-secondary'}
      className={`rounded-pill py-0 px-2 mx-1 ${className}`}
      size="sm"
      onClick={() =>
        setFilters((prev) => ({
          ...prev,
          [filterKey]: prev[filterKey] === value ? null : value,
        }))
      }
    >
      {label}
    </Button>
  );

  const handleSearch = (query) => {
    setTempSearchTerm(query);
    if (query && query !== '') {
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(
        adminActions.getSessions({
          searchQuery: query,
          dateRange,
          nextToken: null,
        })
      );
    } else {
      setSearchTerm(query);
      // Re-fetch with current date range when clearing search
      const dateRange = getDateRangeForFilter(dateRangeFilter);
      dispatch(adminActions.getSessions({ dateRange, nextToken: null }));
    }
  };

  return (
    <Container fluid className="dashboard-container">
      <div className="dashboard-header">
        <div className="d-flex align-items-center mb-3" style={{ gap: '1rem' }}>
          <Link
            to="/admin"
            className="d-flex align-items-center justify-content-center"
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#e9ecef',
              textDecoration: 'none',
              flexShrink: 0,
            }}
            title="Admin Home"
          >
            <svg width="20" height="20" viewBox="0 0 512 512">
              <path
                d="M217.668 439.078h72.7L424 72.486h-74.748L270.4 293.158l-14.336 47.1h-4.1l-13.312-47.1-75.768-220.672H88.132z"
                fill="#dc3545"
              />
            </svg>
          </Link>
          <div
            className="d-flex"
            role="group"
            style={{
              borderRadius: '20px',
              overflow: 'hidden',
              border: '2px solid #dc3545',
              height: '36px',
              width: 'fit-content',
            }}
          >
            <Link
              to="/admin/dashboard"
              className="d-flex align-items-center"
              style={{
                backgroundColor: 'white',
                color: '#dc3545',
                padding: '0 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
                textDecoration: 'none',
              }}
            >
              Sessions
            </Link>
            <span
              className="d-flex align-items-center"
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                padding: '0 1rem',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Live
            </span>
          </div>
          <SearchInput searchTerm={searchTerm} search={handleSearch} />
        </div>
        <div className="filter-container">
          {/* Server-side date range filters */}
          <Button
            variant={
              dateRangeFilter === 'near' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('near')}
          >
            ±7 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+30' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('+30')}
          >
            +30 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === '+90' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('+90')}
          >
            +90 Days
          </Button>
          <Button
            variant={
              dateRangeFilter === 'all' ? 'secondary' : 'outline-secondary'
            }
            className="rounded-pill py-0 px-2 mx-1"
            size="sm"
            onClick={() => setDateRangeFilter('all')}
          >
            All
          </Button>
          <span className="mx-2 text-muted">|</span>
          {/* Client-side filters */}
          <FilterPill label="Male" value="MALE" filterKey="gender" />
          <FilterPill label="Female" value="FEMALE" filterKey="gender" />
          <FilterPill label="Hide Demos" value="hide" filterKey="demo" />
          <FilterPill label="Today" value="today" filterKey="start" />
          <DatePicker
            selectsRange={true}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(update) => {
              const [start, end] = update;
              setFilters((prev) => ({
                ...prev,
                startDate: start,
                endDate: end,
              }));
            }}
            dateFormat="MM/dd/yyyy"
            className="date-picker-pill"
            placeholderText="Date Range"
            isClearable
            customInput={
              <Button
                size="sm"
                variant={filters.startDate ? 'secondary' : 'outline-secondary'}
                className="rounded-pill py-0 px-3"
              >
                {filters.startDate
                  ? filters.endDate
                    ? `${new Date(
                        filters.startDate
                      ).toLocaleDateString()} - ${new Date(
                        filters.endDate
                      ).toLocaleDateString()}`
                    : new Date(filters.startDate).toLocaleDateString()
                  : 'Date Range'}
              </Button>
            }
          />
        </div>
      </div>

      {!alert.clear && alert.message && (
        <Alert
          dismissible
          onClose={() => dispatch(alertActions.clear())}
          variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
          className="dashboard-alert"
        >
          {alert.message}
        </Alert>
      )}

      {/* Add auto-dismiss effect for alerts */}
      {useEffect(() => {
        if (alert.message) {
          const timer = setTimeout(() => {
            dispatch(alertActions.clear());
          }, 5000); // 5 seconds
          return () => clearTimeout(timer);
        }
      }, [alert.message, dispatch])}

      <div className="dashboard-content">
        {isLoading || numSessions === 0 ? (
          <Loading />
        ) : data.length === 0 && !isSearch ? (
          noResults('live sessions')
        ) : (
          <TableComponent
            columns={headers}
            data={data}
            initialState={{
              sortBy: [{ id: 'start', desc: false }],
            }}
            searchTerm={searchTerm}
            textSort={true}
            showFooter={true}
            filterColumns={['name']}
            onClick={(e, row) => {
              if (
                e.target.tagName === 'BUTTON' ||
                e.target.tagName === 'SELECT' ||
                e.target.closest('button') ||
                e.target.closest('select')
              ) {
                return;
              }
              setSelectedSession(row.id);
            }}
            updateMyData={(rowIndex, columnId, value) => {
              setData((old) =>
                old.map((row, index) => {
                  if (index === rowIndex) {
                    return {
                      ...row,
                      [columnId]: value,
                    };
                  }
                  return row;
                })
              );
            }}
          />
        )}
        {isScrollLoading && <ScrollLoading />}
      </div>

      <Modal
        show={selectedSession !== null}
        onHide={() => setSelectedSession(null)}
        size="xl"
        centered
        dialogClassName="session-modal"
        animation={false}
      >
        {selectedSession && (
          <SessionDetail
            key={selectedSession}
            sessionId={selectedSession}
            sessionKey={sessions.byId[selectedSession]?.sessionKey}
            back={() => setSelectedSession(null)}
          />
        )}
      </Modal>

      <StreamCreate
        show={showStreamCreate !== null}
        onHide={() => setShowStreamCreate(null)}
        sessionId={showStreamCreate}
      />
    </Container>
  );
}

export default LiveDashboard;
